import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { AuthLayout, TextInput, Button, Alert } from '../components';

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [method, setMethod] = useState<'Email' | 'Sms'>('Email');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authAPI.forgotPassword({ emailOrPhone, method });
      sessionStorage.setItem('resetEmailOrPhone', emailOrPhone);
      navigate('/verify-otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('forgotPassword.sendOtpFailed', { ns: 'auth' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('forgotPassword.title', { ns: 'auth' })}
      subtitle={t('forgotPassword.subtitle', { ns: 'auth' })}
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && <Alert variant="error" message={error} dismissible onDismiss={() => setError('')} />}

        <TextInput
          id="emailOrPhone"
          label={t('forgotPassword.emailOrPhoneLabel', { ns: 'auth' })}
          type="text"
          placeholder={t('forgotPassword.emailOrPhonePlaceholder', { ns: 'auth' })}
          value={emailOrPhone}
          onChange={(e) => setEmailOrPhone(e.target.value)}
          required
          autoComplete="email"
        />

        <div>
          <label className="mb-3 block text-base font-semibold text-[#2b2b2b] dark:text-[#fffdf8]">
            {t('forgotPassword.deliveryMethodLabel', { ns: 'auth' })}
          </label>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                id="emailMethod"
                type="radio"
                name="method"
                value="Email"
                checked={method === 'Email'}
                onChange={(e) => setMethod(e.target.value as 'Email' | 'Sms')}
                className="h-4 w-4"
              />
              <label htmlFor="emailMethod" className="ml-3 text-base text-[#2b2b2b] dark:text-[#fffdf8]">
                {t('forgotPassword.deliveryMethodEmail', { ns: 'auth' })}
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="smsMethod"
                type="radio"
                name="method"
                value="Sms"
                checked={method === 'Sms'}
                onChange={(e) => setMethod(e.target.value as 'Email' | 'Sms')}
                className="h-4 w-4"
              />
              <label htmlFor="smsMethod" className="ml-3 text-base text-[#2b2b2b] dark:text-[#fffdf8]">
                {t('forgotPassword.deliveryMethodSms', { ns: 'auth' })}
              </label>
            </div>
          </div>
        </div>

        <Button type="submit" variant="primary" size="md" fullWidth isLoading={isLoading}>
          {isLoading ? t('forgotPassword.submitLoading', { ns: 'auth' }) : t('forgotPassword.submitIdle', { ns: 'auth' })}
        </Button>

        <div className="pt-4 text-center">
          <p className="text-base text-[#78716c] dark:text-[#b0a8a0]">
            {t('forgotPassword.rememberedPassword', { ns: 'auth' })}{' '}
            <Link to="/login" className="font-semibold text-[#b64f42] dark:text-[#d4825a] hover:underline">
              {t('forgotPassword.backToLogin', { ns: 'auth' })}
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};
